import { useEffect, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { useEditPartMediaFileDescription } from 'parts/hooks';

import { updateDescriptionSchema } from './schema';

interface IUpdateDescriptionFormValues {
  description: string;
}

interface IProps {
  isOpen: boolean;
  fileId: number;
  partNumber: string;
  defaultDescription: string;
  defaultName: string;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

export const UpdateDescription = ({
  isOpen,
  fileId,
  partNumber,
  defaultDescription,
  defaultName,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { editPartMediaFileDescription } = useEditPartMediaFileDescription({
    position: NotificationPosition.FORM,
  });

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultValues = { description: defaultDescription };

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const onSubmit =
    (reset: UseFormReset<IUpdateDescriptionFormValues>) =>
    async (data: IUpdateDescriptionFormValues) => {
      await editPartMediaFileDescription({
        alt: defaultName,
        description: data.description,
        file: fileId,
        name: defaultName,
        part: partNumber,
      });

      closeTimeoutRef.current = setTimeout(() => {
        handleOnClose(reset);
        onSubmitSuccess();
      }, 1000);
    };

  const handleOnClose = (reset: UseFormReset<IUpdateDescriptionFormValues>) => {
    clearNotification();
    onClose();
    reset(defaultValues);
  };

  return (
    <FormProvider
      schema={updateDescriptionSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        title={t('titleUpdateFile')}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelUpdateFile')}
      >
        <FormField
          name="description"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelDescription') }}
        />
      </Form>
    </FormProvider>
  );
};
